import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  logoutUser,
  getAuthInstance,
  logEvent
} from '../config/firebase'
import { onAuthStateChanged as firebaseOnAuthStateChanged } from 'firebase/auth'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let unsubscribe = null

    const setupAuthListener = async () => {
      try {
        const auth = await getAuthInstance()
        unsubscribe = firebaseOnAuthStateChanged(auth, (user) => {
          setUser(user)
          setLoading(false)

          // Force refresh filters and menu on any auth change
          window.dispatchEvent(new Event('gameFiltersUpdated'))
        })
      } catch (error) {
        console.error('Error setting up auth listener:', error)
        setLoading(false)
      }
    }

    setupAuthListener()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const signInWithGoogleHandler = async () => {
    try {
      const result = await signInWithGoogle()
      logEvent('login', { method: 'google' })
      return result.user
    } catch (error) {
      logEvent('login_failed', { method: 'google', error: error.message })
      throw error
    }
  }

  const signInWithEmailHandler = async (email, password) => {
    try {
      const result = await signInWithEmail(email, password)
      logEvent('login', { method: 'email' })
      return result.user
    } catch (error) {
      logEvent('login_failed', { method: 'email', error: error.message })
      throw error
    }
  }

  const signUpWithEmailHandler = async (email, password) => {
    try {
      const result = await signUpWithEmail(email, password)
      logEvent('sign_up', { method: 'email' })
      return result.user
    } catch (error) {
      logEvent('sign_up_failed', { method: 'email', error: error.message })
      throw error
    }
  }

  const logout = async () => {
    try {
      await logoutUser()
      logEvent('logout')
      const { clearUserId } = await import('../utils/userIdUtils')
      clearUserId()
    } catch (error) {
      throw error
    }
  }

  const value = {
    user,
    loading,
    signInWithGoogle: signInWithGoogleHandler,
    signInWithEmail: signInWithEmailHandler,
    signUpWithEmail: signUpWithEmailHandler,
    logout
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
